<template>
  <div class="ig-employee-print-page">
    <div class="print-toolbar no-print">
      <div>
        <strong>员工登记表打印预览</strong>
        <span>共 {{ employees.length }} 名员工</span>
      </div>
      <div class="toolbar-buttons">
        <!-- <el-button icon="el-icon-arrow-left" @click="goBack">
          返回列表
        </el-button> -->
        <el-button
          type="primary"
          icon="el-icon-printer"
          :disabled="loading || employees.length === 0"
          @click="printPage"
        >
          开始打印
        </el-button>
      </div>
    </div>

    <div v-if="loading" class="print-state no-print">
      <i class="el-icon-loading"></i>
      正在加载员工登记表...
    </div>
    <div v-else-if="employees.length === 0" class="print-state no-print">
      <i class="el-icon-warning-outline"></i>
      未获取到可打印的员工资料
    </div>

    <main v-else class="print-content">
      <article
        v-for="(employee, employeeIndex) in employees"
        :key="`${employee.applyId}-${employeeIndex}`"
        class="employee-print-block"
      >
        <section class="print-page first-page">
          <div class="form-heading">
            <img
              v-if="!isLaborDispatch(employee)"
              class="print-logo"
              src="@/assets/printLogo.png"
              alt="雷士照明"
            />
            <h1>员工登记表</h1>
            <div class="work-number">工号：<span></span></div>
          </div>

          <table class="form-table intro-table">
            <tbody>
              <tr>
                <td class="field-label">应聘职位</td>
                <td>{{ printValue(employee.base.Position) }}</td>
              </tr>
              <tr>
                <td colspan="2" class="notice-signature-cell">
                  <div class="notice-cell">
                    <strong>注：</strong
                    >本人充分了解学历证明、资料证明、工作经历等及以下资料真实是公司聘用的前提并已在最近企业解除劳动合同，如有虚假，公司可立即解除劳动合同，并不予经济补偿。我司食堂使用公共餐盘用餐，入职员工均需符合身体健康要求。
                  </div>
                  <div class="signature-cell">
                    <span>本人签名：</span>
                    <span class="signature-line"></span>
                    <span>年</span>
                    <span class="date-line"></span>
                    <span>月</span>
                    <span class="date-line"></span>
                    <span>日</span>
                  </div>
                </td>
              </tr>
              <tr>
                <td colspan="2" class="choice-cell">
                  <strong>应聘渠道：</strong>
                  <span
                    v-for="channel in channelOptions"
                    :key="channel.label"
                    class="choice-item"
                  >
                    <i
                      class="check-box"
                      :class="{
                        checked: isChannelChecked(employee, channel.keywords),
                      }"
                    ></i>
                    {{ channel.label }}
                  </span>
                  <span class="recommend-info">
                    推荐人及工号：{{
                      combinedValue(
                        getCompatibleField(employee.base, "Introducer"),
                        employee.base.InWorkCode
                      )
                    }}
                  </span>
                  <span
                    v-if="
                      isChannelChecked(employee, ['派遣']) &&
                      employee.base.PqCompany
                    "
                    class="recommend-info"
                  >
                    派遣公司：{{ employee.base.PqCompany }}
                  </span>
                </td>
              </tr>
              <tr>
                <td colspan="2" class="choice-cell split-choice">
                  <span>
                    <strong>是否在雷士工作过：</strong>
                    <i class="check-box"></i>否 <i class="check-box"></i>是
                  </span>
                  <span>
                    <strong>是否有亲属在雷士：</strong>
                    <i class="check-box"></i>否 <i class="check-box"></i>是
                    <span class="inline-fill wide">姓名：</span>
                  </span>
                </td>
              </tr>
              <!-- 无银行卡时：职业病史 + 银行卡(复选框) 同一行 -->
              <tr v-if="!hasBankInfo(employee)">
                <td colspan="2" class="choice-cell split-choice">
                  <span>
                    <strong>是否有职业病史：</strong>
                    <i class="check-box"></i>否 <i class="check-box"></i>是
                    <span class="inline-fill wide">描述：</span>
                  </span>
                  <span>
                    <strong>是否有银行卡：</strong>
                    <i class="check-box"></i>否
                    <i class="check-box"></i>是
                  </span>
                </td>
              </tr>
              <!-- 有银行卡时：职业病史单独一行 -->
              <tr v-else>
                <td colspan="2" class="choice-cell">
                  <strong>是否有职业病史：</strong>
                  <i class="check-box"></i>否 <i class="check-box"></i>是
                  <span class="inline-fill wide">描述：</span>
                </td>
              </tr>
              <!-- 有银行卡时：银行卡单独一行，显示卡号/开户行 -->
              <tr v-if="hasBankInfo(employee)">
                <td colspan="2" class="choice-cell">
                  <strong>是否有银行卡：</strong>
                  是
                  <span class="inline-fill wide">
                    {{
                      combinedValue(
                        employee.base.BankCode,
                        employee.base.Bank
                      )
                    }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          <table class="form-table personal-table">
            <colgroup>
              <col class="label-column" />
              <col class="value-column" />
              <col class="label-column narrow" />
              <col class="value-column narrow" />
              <col class="label-column birth" />
              <col class="value-column birth" />
              <col class="label-column narrow" />
              <col class="value-column narrow" />
              <col class="photo-column" />
            </colgroup>
            <tbody>
              <tr class="section-row">
                <th colspan="9">个人信息</th>
              </tr>
              <tr>
                <td class="field-label">姓名</td>
                <td>{{ printValue(employee.base.Title) }}</td>
                <td class="field-label">性别</td>
                <td>{{ getOptionLabel("Gender", employee.base.Gender) }}</td>
                <td class="field-label">出生年月日</td>
                <td>{{ printValue(employee.base.BirthDate) }}</td>
                <td class="field-label">民族</td>
                <td>{{ getOptionLabel("Nation", employee.base.Nation) }}</td>
                <td rowspan="4" class="photo-cell">照片</td>
              </tr>
              <tr>
                <td class="field-label">籍贯</td>
                <td>{{ printValue(employee.base.NativePlace) }}</td>
                <td class="field-label">户籍性质</td>
                <td>
                  {{
                    getOptionLabel(
                      "householdRegister",
                      employee.base.householdRegister
                    )
                  }}
                </td>
                <td class="field-label">政治面貌</td>
                <td>{{ getOptionLabel("Party", employee.base.Party) }}</td>
                <td class="field-label">婚姻状况</td>
                <td>
                  {{
                    getOptionLabel("MaritalStatus", employee.base.MaritalStatus)
                  }}
                </td>
              </tr>
              <tr>
                <td class="field-label">手机/宅电</td>
                <td colspan="3">{{ printValue(employee.base.MobileNo) }}</td>
                <td class="field-label">身份证号码</td>
                <td colspan="3">{{ printValue(employee.base.IDNo) }}</td>
              </tr>
              <tr>
                <td class="field-label">现住址</td>
                <td colspan="3">{{ printValue(employee.base.LiveAddress) }}</td>
                <td class="field-label">户口所在地</td>
                <td colspan="3">{{ printValue(employee.base.PermanentAddress) }}</td>
              </tr>
              <tr>
                <td class="field-label">紧急联系人</td>
                <td>{{ printValue(employee.emergency.EmContact) }}</td>
                <td class="field-label">关系</td>
                <td>
                  {{
                    getOptionLabel(
                      "EmContactRel",
                      employee.emergency.EmContactRel
                    )
                  }}
                </td>
                <td class="field-label">联系电话</td>
                <td colspan="4">
                  {{ printValue(employee.emergency.EmContactTel) }}
                </td>
              </tr>
            </tbody>
          </table>

          <table class="form-table relation-table">
            <tbody>
              <tr class="section-row">
                <th colspan="5">
                  主要社会关系（已婚者填写配偶、子女情况；未婚者填写父母情况）
                </th>
              </tr>
              <tr class="table-header">
                <th>姓名</th>
                <th>关系</th>
                <th>工作单位及地址</th>
                <th>职业</th>
                <th>联系电话</th>
              </tr>
              <tr v-for="(row, index) in employee.familyRows" :key="index">
                <td>{{ printValue(row.Name) }}</td>
                <td>{{ getOptionLabel("Relation", row.Relation) }}</td>
                <td>
                  {{ combinedValue(row.WorkOrg, row.HJAddress) }}
                </td>
                <td>{{ printValue(row.WorkPost) }}</td>
                <td>{{ printValue(row.TelNo) }}</td>
              </tr>
            </tbody>
          </table>

          <table class="form-table education-table">
            <tbody>
              <tr class="section-row">
                <th colspan="5">
                  教育情况（中专及以上学历者可从高中栏开始填写）
                </th>
              </tr>
              <tr class="table-header">
                <th>学历</th>
                <th>学校名称</th>
                <th>所学专业</th>
                <th>起止时间</th>
                <th>证书编号</th>
              </tr>
              <tr v-for="(row, index) in employee.educationRows" :key="index">
                <td>{{ getOptionLabel("Education", row.Education) }}</td>
                <td>{{ printValue(row.School) }}</td>
                <td>{{ printValue(row.Major) }}</td>
                <td>{{ formatPeriod(row.BeginDate, row.EndDate) }}</td>
                <td>{{ printValue(row.No) }}</td>
              </tr>
            </tbody>
          </table>

          <table class="form-table work-table">
            <tbody>
              <tr class="section-row">
                <th colspan="4">工作经历和其他情况</th>
              </tr>
              <tr class="table-header">
                <th>起止时间</th>
                <th>工作单位及地址</th>
                <th>担任职位</th>
                <th>证明人/电话</th>
              </tr>
              <tr v-for="(row, index) in employee.workRows" :key="index">
                <td>{{ formatPeriod(row.BeginDate, row.EndDate) }}</td>
                <td>
                  {{ combinedValue(row.Organization, row.OrgName) }}
                </td>
                <td>{{ printValue(row.Position) }}</td>
                <td>
                  {{ combinedValue(row.referenceBy, row.referenceTel) }}
                </td>
              </tr>
              <tr class="large-row">
                <td class="large-label">个人特长/爱好及获奖情况</td>
                <td colspan="3"></td>
              </tr>
              <tr class="large-row">
                <td class="large-label">在职/在校接受培训情况</td>
                <td colspan="3"></td>
              </tr>
              <tr class="approval-row">
                <td colspan="2">人力资源签字：</td>
                <td colspan="2">用人部门签字：</td>
              </tr>
            </tbody>
          </table>
        </section>
      </article>
    </main>
  </div>
</template>

<script>
import api from "@/apis";

const OPTION_TYPES = [
  "Gender",
  "Nation",
  "Party",
  "MaritalStatus",
  "EmContactRel",
  "Relation",
  "Education",
  "JoinType",
  "householdRegister",
];

const PRINT_ROW_COUNTS = {
  family: 3,
  education: 4,
  work: 5,
};

// 打印表保持固定行数，数据不足补空行，超出时仅展示前指定条数
function createFixedRows(rows, rowCount) {
  const fixedRows = Array.isArray(rows) ? rows.slice(0, rowCount) : [];
  while (fixedRows.length < rowCount) {
    fixedRows.push({});
  }
  return fixedRows;
}

export default {
  name: "EmployeeInformationPrint",
  data() {
    return {
      loading: false,
      employees: [],
      optionMaps: {},
      channelOptions: [
        { label: "人才市场", keywords: ["人才"] },
        { label: "互联网", keywords: ["互联网", "网络", "网站"] },
        { label: "广告", keywords: ["广告"] },
        { label: "校园招聘", keywords: ["校园", "学校"] },
        { label: "中介", keywords: ["中介", "派遣"] },
        { label: "内部推荐", keywords: ["内部", "推荐", "介绍"] },
      ],
    };
  },
  created() {
    // 优先从 sessionStorage 获取 ID，避免 URL 明文暴露
    const idsValue = this.$route.query.ids || sessionStorage.getItem("printIds") || "";
    // 用完即清，避免残留
    sessionStorage.removeItem("printIds");
    const applyIds = this.parseApplyIds(idsValue);
    if (!applyIds.length) {
      this.$message.warning("页面已经重新刷新，请查询个人信息再打印");
      return;
    }
    this.loadPage(applyIds);
  },
  methods: {
    parseApplyIds(value) {
      const values = Array.isArray(value) ? value : [value];
      return values
        .flatMap((item) => String(item || "").split(","))
        .map((item) => item.trim())
        .filter(Boolean);
    },
    printValue(value) {
      return value === null || value === undefined ? "" : String(value);
    },
    combinedValue(...values) {
      return values
        .map((value) => this.printValue(value).trim())
        .filter(Boolean)
        .join(" / ");
    },
    formatPeriod(beginDate, endDate) {
      const begin = this.printValue(beginDate).trim();
      const end = this.printValue(endDate).trim();
      if (!begin && !end) return "";
      return `${begin}${begin && end ? " 至 " : ""}${end}`;
    },
    getCompatibleField(data, prefix) {
      const target = data || {};
      const key = Object.keys(target).find((item) => item.startsWith(prefix));
      return key ? target[key] : "";
    },
    getOptionLabel(type, value) {
      if (value === null || value === undefined || value === "") return "";
      const options = this.optionMaps[type] || {};
      return options[String(value)] || String(value);
    },
    normalizeOptionMap(data) {
      return Object.values(data || {}).reduce((result, item) => {
        const value = item.ID;
        const label = String(item.Title || "")
          .replace(/\r?\n/g, "")
          .trim();
        if (value && label) {
          result[String(value)] = label;
        }
        return result;
      }, {});
    },
    hasBankInfo(employee) {
      return Boolean(employee.base.BankCode || employee.base.Bank);
    },
    isChannelChecked(employee, keywords) {
      const label = this.getOptionLabel("JoinType", employee.base.Channel);
      return keywords.some((keyword) => label.includes(keyword));
    },
    // 招聘渠道为“劳务派遣”时不展示 Logo
    // 注：后端 Channel 返回数字 1487 对应劳务派遣，同时兼容按 label 匹配
    isLaborDispatch(employee) {
      const channel = employee.base.Channel;
      if (channel === 1487 || String(channel) === "1487") return true;
      const label = this.getOptionLabel("JoinType", channel);
      return label.includes("劳务派遣");
    },
    async loadOptions() {
      const results = await Promise.all(
        OPTION_TYPES.map(async (type) => {
          try {
            const res = await api.getPreEmployeeOptions({ type });
            return {
              type,
              options: this.normalizeOptionMap(res.data),
            };
          } catch (error) {
            return { type, options: {} };
          }
        })
      );
      results.forEach(({ type, options }) => {
        this.$set(this.optionMaps, type, options);
      });
    },
    async loadEmployees(applyIds) {
      const results = await Promise.all(
        applyIds.map(async (applyId) => {
          try {
            const res = await api.getPreEmployeeDetail({ id: applyId });
            const detail = res.data || {};
            return {
              applyId,
              base: detail.preEmpinfo || {},
              emergency: detail.PreBgeminfo || {},
              familyRows: createFixedRows(
                detail.PreFamilyinfo,
                PRINT_ROW_COUNTS.family
              ),
              educationRows: createFixedRows(
                detail.PreEduinfo,
                PRINT_ROW_COUNTS.education
              ),
              workRows: createFixedRows(
                detail.PreWorkexpinfo,
                PRINT_ROW_COUNTS.work
              ),
            };
          } catch (error) {
            return null;
          }
        })
      );
      this.employees = results.filter(Boolean);
      const failedCount = results.length - this.employees.length;
      if (failedCount > 0) {
        this.$message.warning(`${failedCount} 名员工资料加载失败，已跳过`);
      }
    },
    async loadPage(applyIds) {
      this.loading = true;
      try {
        await Promise.all([this.loadOptions(), this.loadEmployees(applyIds)]);
      } finally {
        this.loading = false;
      }
    },
    printPage() {
      this.$nextTick(() => {
        window.print();
      });
    },
    goBack() {
      this.$router.push("/employee/list");
    },
  },
};
</script>

<style lang="scss" scoped>
.ig-employee-print-page {
  min-height: 100vh;
  padding: 24px;
  color: #111;
  background: #fff;
  box-sizing: border-box;
}

.print-toolbar {
  position: sticky;
  z-index: 10;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1100px;
  margin: 0 auto 20px;
  padding: 16px 20px;
  font-size: 24px;
  color: #303133;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 5px 18px rgba(31, 67, 49, 0.12);

  strong {
    margin-right: 16px;
    font-size: 28px;
  }

  span {
    color: #7c8781;
  }
}

.toolbar-buttons {
  display: flex;
  gap: 12px;
}

::v-deep .print-toolbar .el-button {
  margin: 0;
  padding: 13px 20px;
  font-size: 22px;
}

.print-state {
  padding: 100px 20px;
  font-size: 28px;
  text-align: center;
  color: #68736d;

  i {
    margin-right: 10px;
  }
}

.print-page {
  width: 200mm;
  height: 297mm;
  margin: 0 auto 16px;
  padding: 3mm;
  font-family: "KaiTi_GB2312", "楷体_GB2312", KaiTi, STKaiti, serif;
  // 打印字号使用 pt，避免被 postcss-pxtorem 缩小
  font-size: 10.5pt;
  line-height: 1.1;
  background: #fff;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.14);
  box-sizing: border-box;
  break-after: page;
  page-break-after: always;
  overflow: hidden;
}

.employee-print-block:last-child .print-page {
  break-after: auto;
  page-break-after: auto;
}

.form-heading {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 9mm;

  h1 {
    margin: 0;
    font-size: 18pt;
    letter-spacing: 6px;
  }
}

.print-logo {
  position: absolute;
  top: 50%;
  left: 2mm;
  width: 40mm;
  height: auto;
  transform: translateY(-50%);
}

.work-number {
  position: absolute;
  top: 1.5mm;
  right: 2mm;
  font-size: 10.5pt;

  span {
    display: inline-block;
    width: 24mm;
    border-bottom: 1px solid #111;
  }
}

.form-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  border: 0.7pt solid #222;

  th,
  td {
    height: 6.8mm;
    padding: 0.25mm 0.7mm;
    border: 0.7pt solid #222;
    font-weight: 400;
    text-align: center;
    vertical-align: middle;
    overflow-wrap: anywhere;
    box-sizing: border-box;
  }
}

.form-table + .form-table {
  margin-top: -0.7pt;
}

.intro-table,
.relation-table,
.education-table,
.work-table {
  margin-bottom: -0.7pt;
}

.intro-table {
  .field-label {
    width: 42mm;
  }

  tr:first-child td {
    padding-left: 1.5mm;
    text-align: left;
  }
}

.field-label {
  font-weight: 400;
  white-space: nowrap;
}

.notice-signature-cell {
  height: auto !important;
  padding: 0 !important;
}

.notice-cell {
  min-height: 12.5mm;
  padding: 0.8mm 1.5mm;
  font-size: 9.75pt;
  font-weight: 400;
  line-height: 1.2;
  text-align: left !important;
  box-sizing: border-box;

  strong {
    font-weight: 700;
  }
}

.signature-cell {
  height: 9mm;
  padding-right: 2mm;
  line-height: 9mm;
  text-align: right !important;
  white-space: nowrap;
  box-sizing: border-box;

  span {
    margin-left: 4px;
  }
}

.signature-line {
  display: inline-block;
  width: 40mm;
  border-bottom: 1px solid #111;
}

.date-line {
  display: inline-block;
  width: 8mm;
  border-bottom: 1px solid #111;
}

.choice-cell {
  height: 6.5mm !important;
  padding-left: 1.5mm !important;
  font-size: 9.75pt;
  line-height: 1.1;
  text-align: left !important;
}

.choice-item {
  display: inline-flex;
  align-items: center;
  margin-right: 3mm;
  white-space: nowrap;
}

.check-box {
  position: relative;
  display: inline-block;
  width: 2.8mm;
  height: 2.8mm;
  margin: 0 0.8mm 0 1.5mm;
  border: 0.7pt solid #222;
  vertical-align: -0.5mm;
  box-sizing: border-box;
}

.check-box.checked::after {
  position: absolute;
  top: -5px;
  left: 0;
  font-family: Arial, sans-serif;
  font-size: 10.5pt;
  font-weight: 700;
  content: "✓";
}

.recommend-info {
  white-space: nowrap;
}

.split-choice {
  white-space: nowrap;

  > span {
    display: inline-flex;
    align-items: center;
    width: 50%;
    vertical-align: middle;
    box-sizing: border-box;
  }
}

.inline-fill {
  display: inline-block;
  min-width: 22mm;
  margin-left: 3mm;
  border-bottom: 1px solid #111;
}

.inline-fill.wide {
  min-width: 38mm;
}

.personal-table {
  .label-column {
    width: 12%;
  }

  .value-column {
    width: 12%;
  }

  .label-column.narrow {
    width: 10%;
  }

  .value-column.narrow {
    width: 9%;
  }

  .label-column.birth {
    width: 13%;
  }

  .value-column.birth {
    width: 14%;
  }

  .photo-column {
    width: 10%;
  }
}

.section-row {
  th {
    height: 6.8mm;
    padding-left: 1.2mm;
    font-size: 12pt;
    font-weight: 600;
    text-align: left;
  }
}

.photo-cell {
  font-size: 10.5pt;
  letter-spacing: 4mm;
  writing-mode: vertical-rl;
}

.relation-table {
  th:nth-child(1) {
    width: 13%;
  }

  th:nth-child(2) {
    width: 12%;
  }

  th:nth-child(3) {
    width: 34%;
  }

  th:nth-child(4) {
    width: 18%;
  }

  th:nth-child(5) {
    width: 23%;
  }
}

.education-table {
  th:nth-child(1) {
    width: 18%;
  }

  th:nth-child(2) {
    width: 28%;
  }

  th:nth-child(3) {
    width: 24%;
  }

  th:nth-child(4) {
    width: 18%;
  }

  th:nth-child(5) {
    width: 12%;
  }
}

.table-header th {
  height: 6.8mm;
  font-size: 10.5pt;
  font-weight: 600;
}

.work-table {
  th:nth-child(1) {
    width: 18%;
  }

  th:nth-child(2) {
    width: 50%;
  }

  th:nth-child(3) {
    width: 15%;
  }

  th:nth-child(4) {
    width: 17%;
  }
}

.large-row {
  td {
    height: 11mm;
  }
}

.large-label {
  width: 18%;
  padding: 1.2mm !important;
  font-size: 9.75pt;
}

.approval-row {
  td {
    height: 12mm;
    padding: 1.5mm !important;
    font-size: 10.5pt;
    text-align: left;
    vertical-align: top;
  }
}

@media (max-width: 900px) {
  .ig-employee-print-page {
    padding: 12px;
    overflow-x: auto;
  }

  .print-toolbar {
    align-items: flex-start;
    flex-direction: column;
    min-width: 720px;
    gap: 14px;
  }
}

@page {
  size: A4 portrait;
  /* 四周留 5mm 外边距，让内容块在纸上居中 */
  margin: 5mm;
}

@media print {
  /* 消除浏览器默认的 body/html 外边距，避免干扰 @page margin */
  html,
  body {
    margin: 0;
    padding: 0;
  }

  .ig-employee-print-page {
    min-height: 0;
    padding: 0;
    background: #fff;
  }

  .no-print {
    display: none !important;
  }

  .print-page {
    width: 200mm;
    /* A4 高 297mm − @page 上下 margin 各 5mm = 287mm，再留 1mm 安全余量防溢出 */
    height: 286mm;
    margin: 0 auto;
    padding: 3mm;
    box-shadow: none;
    overflow: hidden;
  }

  .employee-print-block {
    break-inside: avoid;
    page-break-inside: avoid;
  }
}
</style>
